import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { selectAreaFor } from "@/store/selections-selectors";
import { RootState } from "@/store/store";
import { isLocationMarker } from "@/types/ielement-extra-types";
import { isIElementViewableGaussianSplats } from "@/types/type-guards";
import {
  IElement,
  IElementGenericPointCloud,
  IElementGenericPointCloudStream,
  IElementRoomSection,
  IElementSection,
  IElementTimeSeries,
  isIElementGenericDataset,
  isIElementGenericOdometryPath,
  isIElementRoomSection,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  isSingleScanSection,
  PointCloudType,
  selectAreaDataSessions,
  selectAreaRoomsData,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectDataSessionContainedData,
  selectPointCloudType,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";

export type DataSessions3D = {
  /** The DataSessions that contains only point clouds */
  pointClouds: IElementSection[];
  /** The DataSessions that contains only gaussian splats */
  gaussianSplats: IElementSection[];
};

export type CurrentAreaData = {
  /** The current area the user is navigating. Undefined when the user is navigating the "entire project" */
  area?: IElementSection;

  /** All the available DataSessions for this area (includes video recordings) */
  dataSessions: IElementSection[];

  /** The DataSessions that contains only 3d data */
  dataSessions3d: DataSessions3D;

  /** The DataSessions that contains only 2d data (Eg. VideoRecordings)*/
  dataSessions2d: IElementSection[];

  /** The DataSessions that contains 3d and 2d data */
  dataSessions5d: IElementSection[];

  /** The available rooms time series in this area */
  roomsTimeSeries: IElementTimeSeries[];

  /** The available rooms in this area */
  roomsSections: IElementSection[];

  /** The available scans of this area */
  scans: IElementSection[];

  /** The available location markers in this area */
  locationMarkers: IElementRoomSection[];

  /** All the room sections that contains 2D photos only (under group attachment) */
  roomsWithImg2d: IElementRoomSection[];
};

/**
 * A memoized selector to compute the current area data based on the active element.
 * Used by the root components of the main modes of the application.
 *
 * @param state The application store state
 * @param activeElement The active element to compute the area data for
 * @returns the memoized selector
 */
export const selectCurrentAreaData = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, activeElement?: IElement) => activeElement,
    ],
    computeCurrentAreaData,
  ),
);

/**
 * The expensive computation of the 'selectCurrentAreaData' selector.
 *
 * @param iElements the iElements store slice
 * @param activeElement the element selected by the user
 * @returns The current area for the active element and all the other interesting elements for that area
 */
function computeCurrentAreaData(
  iElements: IElementsState,
  activeElement?: IElement,
): CurrentAreaData | undefined {
  if (!activeElement) {
    return;
  }

  const area = selectAreaFor(activeElement)({ iElements });

  if (!area) return;
  const dataSessions = selectAreaDataSessions(area)({ iElements });

  const scans: IElementSection[] = [];
  const dataSessions2d: IElementSection[] = [];
  const dataSessions3d: DataSessions3D = {
    pointClouds: [],
    gaussianSplats: [],
  };
  const dataSessions5d: IElementSection[] = [];
  for (const session of dataSessions) {
    const { panos, pointClouds, gaussianSplats } =
      selectDataSessionContainedData(session, {
        pointCloudFilter: (pc) => selectIsPointCloudViewable(pc)({ iElements }),
        gaussianSplatsFilter: isIElementViewableGaussianSplats,
      })({ iElements });

    // Rovi scans have paths, so they should be considered 2d as well
    const paths = selectChildDepthFirst(
      session,
      isIElementGenericOdometryPath,
    )({ iElements });

    const has2dData = panos || paths;

    if (has2dData && pointClouds) {
      dataSessions5d.push(session);
    } else if (has2dData) {
      dataSessions2d.push(session);
    } else if (pointClouds) {
      dataSessions3d.pointClouds.push(session);
    }

    if (gaussianSplats) {
      dataSessions3d.gaussianSplats.push(session);
    }
    if (isIElementGenericDataset(session)) {
      scans.push(
        ...selectChildrenDepthFirst(
          session,
          isSingleScanSection,
        )({ iElements }),
      );
    }
  }

  const areaRoomsData = selectAreaRoomsData(area)({ iElements });

  const locationMarkers = selectChildrenDepthFirst(
    area,
    isIElementRoomSection,
  )({ iElements }).filter((roomSection) =>
    isLocationMarker(roomSection, { iElements }),
  );

  return {
    area,
    dataSessions,
    dataSessions2d,
    dataSessions3d,
    dataSessions5d,
    scans,
    roomsTimeSeries: areaRoomsData.roomsTimeSeries,
    roomsSections: areaRoomsData.roomsSections,
    locationMarkers,
    roomsWithImg2d: areaRoomsData.roomsWithImg2d,
  };
}

/**
 * @returns whether a given point cloud should be viewable in the Viewer, or hidden from the user completely.
 * @param pc the point cloud to check
 */
export function selectIsPointCloudViewable(
  pc: IElementGenericPointCloudStream | IElementGenericPointCloud,
) {
  return (state: OnlyIElementsStore): boolean =>
    selectPointCloudType(pc)(state) !== PointCloudType.singleScan;
}
